import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Actions,
  CloseButton,
  CompleteButton,
  Description,
  Header,
  PointsBadge,
  PointsInfo,
  Title,
  TooltipContainer,
} from './styles';

import { ChevronRightIcon, CrossIcon } from '../../../assets/svg/SVGComponents';
import SecondaryButton from '../../Buttons/SecondaryButton';
import PrimaryButton from '../../Buttons/PrimaryButton';

interface OnboardingTooltipProps {
  title: string;
  description: string;
  points?: number;
  targetSelector: string | null;
  position: string;
  isActive: boolean;
  onComplete: () => void;
  onClose: () => void;
  onSkip: () => void;
  isLastStep: boolean;
  isButtonEnabled?: boolean;
  requiredActionActive?: boolean;
}

const OnboardingTooltip: React.FC<OnboardingTooltipProps> = ({
  title,
  description,
  points,
  targetSelector,
  position,
  isActive,
  onComplete,
  onClose,
  onSkip,
  isLastStep,
  isButtonEnabled = true,
  requiredActionActive = false,
}) => {
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const updatePosition = useCallback((): void => {
    if (tooltipRef.current) {
      let top = 0;
      let left = 0;

      if (targetSelector) {
        const targetElement = document.querySelector(targetSelector);

        if (targetElement) {
          const targetRect = targetElement.getBoundingClientRect();
          const tooltipRect = tooltipRef.current.getBoundingClientRect();

          // Calcular posição com base na preferência
          switch (position) {
            case 'top':
              top = targetRect.top - tooltipRect.height - 10 + window.scrollY;
              left =
                targetRect.left +
                targetRect.width / 2 -
                tooltipRect.width / 2 +
                window.scrollX;
              break;
            case 'right':
              top =
                targetRect.top +
                targetRect.height / 2 -
                tooltipRect.height / 2 +
                window.scrollY;
              left = targetRect.right + 10 + window.scrollX;
              break;
            case 'bottom':
              top = targetRect.bottom + 10 + window.scrollY;
              left =
                targetRect.left +
                targetRect.width / 2 -
                tooltipRect.width / 2 +
                window.scrollX;
              break;
            case 'left':
              top =
                targetRect.top +
                targetRect.height / 2 -
                tooltipRect.height / 2 +
                window.scrollY;
              left = targetRect.left - tooltipRect.width - 10 + window.scrollX;
              break;
            default:
              // Centro da tela se a posição não for reconhecida
              top =
                window.innerHeight / 2 -
                tooltipRect.height / 2 +
                window.scrollY;
              left =
                window.innerWidth / 2 - tooltipRect.width / 2 + window.scrollX;
          }

          // Ajustar se estiver saindo da tela
          if (left < 10) left = 10;
          if (left + tooltipRect.width > window.innerWidth - 10) {
            left = window.innerWidth - tooltipRect.width - 10;
          }

          if (top < 10) top = 10;
          if (top + tooltipRect.height > window.innerHeight - 10) {
            top = window.innerHeight - tooltipRect.height - 10;
          }
        }
      }
      if (!targetSelector) {
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        top = window.innerHeight / 2 - tooltipRect.height / 2 + window.scrollY;
        left = window.innerWidth / 2 - tooltipRect.width / 2 + window.scrollX;
      }
      setTooltipPosition({ top, left });
    }
  }, [position, targetSelector]);

  useEffect(() => {
    if (!isActive) return;

    // Observer para detectar mudanças no DOM
    const observer = new MutationObserver(() => {
      setTimeout(updatePosition, 100);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // Atualizar posição inicialmente
    setTimeout(updatePosition, 100);

    // Atualizar periodicamente (reduzido para 1s para melhor performance)
    const interval = setInterval(updatePosition, 1000);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [targetSelector, position, isActive, updatePosition]);

  if (!isActive) return null;

  let completeButtonText: string;
  if (isLastStep) {
    completeButtonText = t('buttons.finish_tour');
  } else if (requiredActionActive) {
    completeButtonText = t('buttons.perform_action');
  } else {
    completeButtonText = t('buttons.continue');
  }

  return createPortal(
    <TooltipContainer
      ref={tooltipRef}
      style={{
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`,
      }}
      $position={position}
    >
      <Header>
        <Title>{title}</Title>
        <CloseButton onClick={onClose}>
          <CrossIcon
            stroke="#1E88E5"
            strokeWidth={2}
            width="1rem"
            height="1rem"
          />
        </CloseButton>
      </Header>

      <Description>{description}</Description>

      {points && (
        <PointsInfo>
          <PointsBadge>+{points} pontos</PointsBadge>
        </PointsInfo>
      )}

      <Actions>
        <SecondaryButton type="button" onClick={onSkip}>
          {t('buttons.skip_tour')}
        </SecondaryButton>

        <PrimaryButton
          onClick={onComplete}
          disabled={!isButtonEnabled}
          className={`primary-button onboarding-continue-button ${
            requiredActionActive ? 'secondary' : ''
          } ${isButtonEnabled ? '' : 'disabled'}`}
        >
          {completeButtonText}
          <ChevronRightIcon
            stroke="#FFF"
            strokeWidth={2}
            width="1rem"
            height="1rem"
          />
        </PrimaryButton>
      </Actions>
    </TooltipContainer>,
    document.body,
  );
};

export default OnboardingTooltip;
